import type { UseMutationResult, QueryClient } from '@tanstack/react-query';
import { useCallback, type Dispatch, type SetStateAction } from 'react';

import {
    activateScenarioViewSwitch,
    clearOptimisticViewListSelection,
    clearScenarioWorkspaceCache,
    type SelectedViewFromList,
} from 'hooks/ppa/ppaViewPrefetch.helpers';
import type { SavedViewListLoadingAction } from 'components/molecules/PricingSimulatorV3/SavedViewStateSelect/SavedViewStateSelect.types';
import type { PPADeleteViewResponse, PPAViewDataSource, PPAViewListItem } from 'types/ppaViews';
import { sendNotification } from 'utils/helperMethods';
import { resolvePpaViewApiErrorMessage } from 'utils/ppaHelpers';

export type UseSavedViewDeleteParams = {
    scenarioId?: number;
    userId: string | null;
    activeViewId: number | null;
    expandedViewId: number | null;
    isScenarioOwner: boolean;
    queryClient: QueryClient;
    deleteViewMutation: UseMutationResult<PPADeleteViewResponse, Error, { view_id: number }>;
    startViewActionLoading: (viewId: number, action: SavedViewListLoadingAction) => void;
    clearViewActionLoading: () => void;
    setExpandedViewId: Dispatch<SetStateAction<number | null>>;
    setHasNoViewSelected: (value: boolean) => void;
    setWorkspaceViewId: (viewId: number | null) => void;
    setActiveDataSource: (dataSource: PPAViewDataSource) => void;
    onSelectedViewChange?: (selection: SelectedViewFromList) => void;
    onLoadedViewChange?: (loadedViewId: number | null, isCustomView: boolean, viewName?: string) => void;
    onViewLoaded?: () => void;
    showViewDeletedNotification: (viewName: string) => void;
};

export function useSavedViewDelete({
    scenarioId,
    userId,
    activeViewId,
    expandedViewId,
    isScenarioOwner,
    queryClient,
    deleteViewMutation,
    startViewActionLoading,
    clearViewActionLoading,
    setExpandedViewId,
    setHasNoViewSelected,
    setWorkspaceViewId,
    setActiveDataSource,
    onSelectedViewChange,
    onLoadedViewChange,
    onViewLoaded,
    showViewDeletedNotification,
}: UseSavedViewDeleteParams) {
    const activateAndLoadView = useCallback(
        async (viewId: number): Promise<boolean> => {
            if (scenarioId == null) {
                return false;
            }

            await activateScenarioViewSwitch(queryClient, {
                scenarioId,
                viewId,
                isScenarioOwner,
                userId,
            });

            setHasNoViewSelected(false);
            setActiveDataSource('view');
            setWorkspaceViewId(viewId);
            onSelectedViewChange?.({ viewId, dataSource: 'view' });
            onViewLoaded?.();
            return true;
        },
        [
            isScenarioOwner,
            onSelectedViewChange,
            onViewLoaded,
            queryClient,
            scenarioId,
            setActiveDataSource,
            setHasNoViewSelected,
            setWorkspaceViewId,
            userId,
        ],
    );

    const executeDeleteView = useCallback(
        async (view: PPAViewListItem) => {
            startViewActionLoading(view.view_id, 'delete');
            const wasActiveView = activeViewId === view.view_id;

            try {
                const response = await deleteViewMutation.mutateAsync({
                    view_id: view.view_id,
                });

                if (response.fallback_applied && scenarioId != null) {
                    clearScenarioWorkspaceCache(queryClient, scenarioId, userId, {
                        includeViewList: false,
                    });
                }

                if (wasActiveView) {
                    if (
                        response.fallback_applied &&
                        response.fallback_view_id != null &&
                        scenarioId != null
                    ) {
                        await activateAndLoadView(response.fallback_view_id);
                    } else {
                        setHasNoViewSelected(true);
                        setWorkspaceViewId(null);
                        setActiveDataSource('view');
                        if (scenarioId != null) {
                            clearOptimisticViewListSelection(queryClient, scenarioId, userId);
                        }
                        onSelectedViewChange?.({ viewId: null, dataSource: 'view' });
                        onLoadedViewChange?.(null, false, undefined);
                    }
                }

                if (expandedViewId === view.view_id) {
                    setExpandedViewId(null);
                }

                showViewDeletedNotification(view.view_name);
            } catch (error) {
                sendNotification(
                    'error',
                    resolvePpaViewApiErrorMessage(error, 'Failed to delete view. Please try again.'),
                );
                throw error;
            } finally {
                clearViewActionLoading();
            }
        },
        [
            activateAndLoadView,
            activeViewId,
            clearViewActionLoading,
            deleteViewMutation,
            expandedViewId,
            onLoadedViewChange,
            onSelectedViewChange,
            queryClient,
            scenarioId,
            startViewActionLoading,
            setActiveDataSource,
            setExpandedViewId,
            setHasNoViewSelected,
            setWorkspaceViewId,
            showViewDeletedNotification,
            userId,
        ],
    );

    return {
        activateAndLoadView,
        handleDeleteView: executeDeleteView,
    };
}
